import { Order, SharedProps } from '@/types'
import { getCustomerInfo } from '@/utils/format'
import { Head, router, usePage } from '@inertiajs/react'
import { Badge, Group, Paper, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { IconPackage, IconTruckDelivery, IconTruckLoading } from '@tabler/icons-react'
import dayjs from 'dayjs'

interface DeliveriesProps extends SharedProps {
  to_pickup: Order[]
  to_deliver: Order[]
}

export default function DeliveriesIndex({ to_pickup, to_deliver }: DeliveriesProps) {
  const { current_organization } = usePage<SharedProps>().props

  const handleRowClick = (orderId: number) =>
    router.get(`/organizations/${current_organization?.slug}/orders/${orderId}`)

  return (
    <>
      <Head title={`Livraison - ${current_organization?.name}`} />

      <Stack gap="xl">
        <Title>Livraison</Title>

        <Stack gap="md">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="blue" size="lg" radius="md">
              <IconTruckDelivery size={20} />
            </ThemeIcon>
            <Title order={3}>À récupérer</Title>
            <Badge color="blue" variant="light">
              {to_pickup.length}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Commandes prêtes à l'atelier, à recevoir en boutique.
          </Text>
          <DeliveriesTable
            orders={to_pickup}
            emptyMessage="Aucune commande à récupérer pour le moment."
            onRowClick={handleRowClick}
          />
        </Stack>

        <Stack gap="md">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="orange" size="lg" radius="md">
              <IconTruckLoading size={20} />
            </ThemeIcon>
            <Title order={3}>À livrer</Title>
            <Badge color="orange" variant="light">
              {to_deliver.length}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Commandes à donner au coursier pour envoi à l'atelier.
          </Text>
          <DeliveriesTable
            orders={to_deliver}
            emptyMessage="Aucune commande à livrer pour le moment."
            onRowClick={handleRowClick}
          />
        </Stack>
      </Stack>
    </>
  )
}

function DeliveriesTable({
  orders,
  emptyMessage,
  onRowClick,
}: {
  orders: Order[]
  emptyMessage: string
  onRowClick: (orderId: number) => void
}) {
  if (orders.length === 0) {
    return (
      <Paper withBorder radius="md" py={40} px="md">
        <Stack align="center" gap="xs">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray">
            <IconPackage size={24} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">
            {emptyMessage}
          </Text>
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
      <Table.ScrollContainer minWidth={600}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>N°</Table.Th>
              <Table.Th>Client</Table.Th>
              <Table.Th>Articles</Table.Th>
              <Table.Th visibleFrom="sm" w={160}>
                Date de remise
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orders.map((order) => (
              <Table.Tr
                key={order.id}
                onClick={() => onRowClick(order.id)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>#{order.id}</Table.Td>
                <Table.Td>{getCustomerInfo(order).name}</Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    {order.articles.map((article) => (
                      <div key={article.id}>
                        <Text size="sm" fw={500}>
                          {article.article_name}
                        </Text>
                        {article.services.map((service) => (
                          <Badge key={service.id} variant="dot" size="xs" color="gray" ml={8}>
                            {service.service_name}
                          </Badge>
                        ))}
                      </div>
                    ))}
                  </Stack>
                </Table.Td>
                <Table.Td visibleFrom="sm">
                  {order.planned_date ? dayjs(order.planned_date).format('DD/MM/YYYY') : '—'}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Paper>
  )
}
